import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import AdminLoginPage from './AdminLoginPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GapTestAdminPage() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filterBP, setFilterBP] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Check existing session on mount
  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setAuthChecked(true));
  }, []);

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterBP !== 'all') params.set('break_point', filterBP);
      if (dateFrom) params.set('date_from', new Date(dateFrom).toISOString());
      if (dateTo) params.set('date_to', new Date(dateTo + 'T23:59:59').toISOString());
      const res = await axios.get(`${API}/gap-test/admin/submissions?${params}`, { withCredentials: true });
      setSubmissions(res.data);
    } catch (err) {
      console.error('Failed to fetch submissions', err);
    } finally {
      setLoading(false);
    }
  }, [filterBP, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (!authChecked) return <div style={{ fontFamily: 'Geist, Arial, sans-serif', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#637989' }}>Loading...</div>;
  if (!user) return <AdminLoginPage onLogin={setUser} />;

  return (
    <div style={{ fontFamily: 'Geist, Arial, sans-serif', background: '#F0F4F8', minHeight: '100vh', padding: '24px' }} data-testid="gap-test-admin">
      <Helmet><title>Gap Test Admin · APIG</title></Helmet>

      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <h1 style={{ fontSize: 24, fontWeight: 600, color: '#0C1824', margin: 0 }}>Execution Gap Test — Submissions</h1>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <a href={`${API}/gap-test/admin/export-csv`} style={{ background: '#0C1824', color: '#fff', padding: '10px 20px', fontSize: 13, fontWeight: 600, textDecoration: 'none', borderRadius: 4 }} data-testid="export-csv">Export CSV</a>
            <button onClick={handleLogout} style={{ background: 'none', border: '1px solid #DBE6EC', padding: '10px 16px', fontSize: 13, color: '#637989', cursor: 'pointer', borderRadius: 4 }} data-testid="logout-btn">Sign Out</button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap', alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#637989', display: 'block', marginBottom: 4 }}>Break Point</label>
            <select value={filterBP} onChange={e => setFilterBP(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #DBE6EC', fontSize: 14, background: '#fff' }} data-testid="filter-bp">
              <option value="all">All</option>
              <option value="Validate">Validate</option>
              <option value="Design">Design</option>
              <option value="Activate">Activate</option>
              <option value="None">None</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#637989', display: 'block', marginBottom: 4 }}>From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #DBE6EC', fontSize: 14 }} data-testid="filter-from" />
          </div>
          <div>
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#637989', display: 'block', marginBottom: 4 }}>To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #DBE6EC', fontSize: 14 }} data-testid="filter-to" />
          </div>
        </div>

        {/* Table */}
        <div style={{ background: '#fff', border: '1px solid #DBE6EC', overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#637989' }}>Loading...</div>
          ) : submissions.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#637989' }}>No submissions yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#0C1824', color: '#fff' }}>
                  {['Date', 'Name', 'Company', 'Title', 'Email', 'Break Point', 'Capital', 'Overall', 'Opt-in', 'Email'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {submissions.map((s, i) => {
                  const c = s.contact || {};
                  return (
                    <tr
                      key={i}
                      onClick={() => setSelected(s)}
                      style={{ cursor: 'pointer', borderBottom: '1px solid #DBE6EC', background: i % 2 === 0 ? '#fff' : '#F8FAFC' }}
                      data-testid={`submission-row-${i}`}
                    >
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{fmtDate(s.receivedAt)}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{c.firstName} {c.lastName}</td>
                      <td style={{ padding: '10px 12px' }}>{c.company}</td>
                      <td style={{ padding: '10px 12px' }}>{c.title}</td>
                      <td style={{ padding: '10px 12px' }}>{c.email}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: s.breakPoint === 'None' ? '#637989' : '#D56A0B' }}>{s.breakPoint}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{s.decision?.capitalAtStake}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 600 }}>{s.scores?.overall}%</td>
                      <td style={{ padding: '10px 12px' }}>{s.consentMarketing ? 'Yes' : 'No'}</td>
                      <td style={{ padding: '10px 12px', fontSize: 11 }}>{s.emailStatus}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <p style={{ fontSize: 12, color: '#637989', marginTop: 12 }}>{submissions.length} submission{submissions.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Detail Modal */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 1000 }} onClick={() => setSelected(null)}>
          <div style={{ background: '#fff', maxWidth: 700, width: '100%', maxHeight: '85vh', overflow: 'auto', padding: 32, borderRadius: 4 }} onClick={e => e.stopPropagation()} data-testid="submission-detail">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 20, fontWeight: 600, color: '#0C1824', margin: 0 }}>{selected.contact?.firstName} {selected.contact?.lastName}</h2>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#637989' }}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px 16px', fontSize: 14 }}>
              {[
                ['Company', selected.contact?.company],
                ['Title', selected.contact?.title],
                ['Email', selected.contact?.email],
                ['Phone', selected.contact?.phone || '—'],
                ['Decision', selected.decision?.text],
                ['Type', selected.decision?.type],
                ['Capital', selected.decision?.capitalAtStake],
                ['Timing', selected.decision?.timing],
                ['Validate', `${selected.scores?.validate}%`],
                ['Design', `${selected.scores?.design}%`],
                ['Activate', `${selected.scores?.activate}%`],
                ['Overall', `${selected.scores?.overall}%`],
                ['Break Point', selected.breakPoint],
                ['Verdict', selected.verdict],
                ['Email Status', selected.emailStatus],
                ['Newsletter', selected.consentMarketing ? 'Yes' : 'No'],
                ['Received', fmtDate(selected.receivedAt)],
              ].map(([label, val]) => (
                <React.Fragment key={label}>
                  <div style={{ fontWeight: 600, color: '#0C1824' }}>{label}</div>
                  <div style={{ color: '#334155' }}>{val}</div>
                </React.Fragment>
              ))}
            </div>

            {selected.openItems?.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#D56A0B', marginBottom: 8 }}>Open Items</h3>
                {selected.openItems.map((item, i) => (
                  <div key={i} style={{ fontSize: 13, color: '#334155', padding: '4px 0', borderBottom: '1px solid #F0F4F8' }}>
                    {item.gate}: {item.claim} ({item.rating})
                  </div>
                ))}
              </div>
            )}

            {selected.answers?.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#637989', marginBottom: 8 }}>All 15 Answers</h3>
                {selected.answers.map((ans, i) => (
                  <div key={i} style={{ fontSize: 13, color: '#334155', padding: '4px 0', borderBottom: '1px solid #F0F4F8', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{ans.gate}: {ans.claim}</span>
                    <span style={{ fontWeight: 600, whiteSpace: 'nowrap', marginLeft: 12 }}>{ans.rating} ({ans.value})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
